import {
  Box,
  Button,
  Card,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  Log,
  approvePendingTerminal,
  getPendingTerminalFilterConditions,
  getPendingTerminalList,
  getTerminalDetailsWithActions,
} from "../../services/services";
import ListHead from "../table/ListHead";
import Scrollbar from "../table/Scrollbar";
import RejectPendingTerminal from "./Dialogs/RejectPendingTerminal";
import { applySortFilter } from "../../utils/tableFunctions";
import { StyledTableRow } from "../TableRow";
import { useLoadingSpinnerContext } from "../../context/loadingSpinnerContext";
import { CustomTableCell } from "../TableCell";
import CommonDialog from "../CustomDialog";
import TerminalDetails from "../../pages/details/admin/TerminalDetails";
import CircularLoader from "../circularLoader";
import FormSearch from "../FormSearch";
import { styles } from "../../utils/styles";
import { TablePagination } from "@mui/material";
import { pageNumber, rowsPerPage as defaultRowsPerPage } from "../../utils/constants";

const TABLE_HEAD = [
  { id: "terminal_id", label: "Terminal ID" },
  { id: "merchantName", label: "Merchant" },
  { id: "business_entity", label: "Business Entity" },
  { id: "mode", label: "Mode" },
  { id: "action", label: "Action" },
  { id: "approve" },
];

const PendingTerminal = ({ userRole }) => {
  const { setGlobalLoading } = useLoadingSpinnerContext();
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [terminalList, setTerminalList] = useState({ results: [], count: 0 });
  const [loaded, setLoaded] = useState(false);
  const [pendingDetails, setPendingDetails] = useState(null);
  const [pagination, setPagination] = useState({
    page: pageNumber,
    page_size: defaultRowsPerPage,
  });

  // filter
  const [terminalFilter, setTerminalFilter] = useState({});
  const [fLoaded, setFLoaded] = useState(false);
  const [filter, setFilter] = useState({});
  const [inputValues, setInputValues] = useState({});

  // dialogs
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [selectedId, setSelectedId] = useState();

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const filteredList = applySortFilter(
    terminalList.results || [],
    order,
    orderBy
  );

  const isItemNotFound = filteredList.length === 0;

  const renderData = data => {
    const newData = data.results.map(item => ({
      ...item,
      merchantName: item.merchant?.name,
      mode: item.mode?.mode,
      business_entity: item.merchant?.business_entity?.business_name
    }))
    setTerminalList({
      ...data,
      results: newData
    })
  }

  const queryPage = (queryParams, params) => {
    setLoaded(false);
    getPendingTerminalList({
      setResponse: renderData,
      setLoaded: setLoaded,
      filter: params || filter,
      queryParams: queryParams
    });
    setPagination(queryParams);
  };

  const handleChangePage = (event, newPage) => {
    queryPage({
      page: newPage + 1,
      page_size: pagination.page_size,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    queryPage({
      page: 1,
      page_size: parseInt(event.target.value, 10),
    });
  };

  useEffect(() => {
    queryPage(pagination);
    getPendingTerminalFilterConditions("pending", setTerminalFilter, setFLoaded);
  }, []);

  const onFilterSubmit = (data) => {
    const newParams = {
      terminal_id: data.terminal_id,
      merchant: data.merchant?.id,
      business_entity: data.business_entity?.id,
      payment_method: data.payment_method?.id,
      terminal_id_input: inputValues?.terminal_id?.trim() || null,
      merchant_input: inputValues?.merchant?.trim() || null,
      business_entity_input: inputValues?.business_entity?.trim() || null,
    };
    const newPagination = {
      ...pagination,
      page: 1,
    };

    Log.info("Click button [Apply Filter] - Search Pending Terminal list", newParams);

    setFilter(newParams);
    queryPage(newPagination, newParams);
  };

  const onFilterReset = () => {
    setInputValues({});
    setFilter({});
    queryPage({ ...pagination, page: 1 }, {});
  };

  const schema = [
    {
      type: "search-select",
      label: "Terminal ID",
      param: "terminal_id",
      options: terminalFilter?.terminal_id?.map((terminal_id) => terminal_id),
      inputValues,
      setInputValues,
    },
    {
      type: "search-select",
      label: "Merchant",
      param: "merchant",
      options: terminalFilter?.merchant?.map((merchant) => ({
        label: merchant.merchant__name,
        id: merchant.merchant__id,
      })),
      inputValues,
      setInputValues,
    },
    {
      type: "search-select",
      label: "Business Entity",
      param: "business_entity",
      options: terminalFilter?.business_entity?.map((entity) => ({
        label: entity.merchant__business_entity__business_name,
        id: entity.merchant__business_entity__id,
      })),
      inputValues,
      setInputValues,
    },
    {
      type: "search-select",
      label: "Method",
      param: "payment_method",
      options: terminalFilter?.payment_method?.map((method) => ({
        label: method.name,
        id: method.id,
      })),
    },
  ];

  const handleCloseDialog = (shouldResetDetails) => {
    setOpenRejectDialog(false);
    if (shouldResetDetails === true) setPendingDetails(null);
  };
  return (
    <Box>
      <FormSearch
        schema={schema}
        loading={!fLoaded}
        onSearch={onFilterSubmit}
        onReset={onFilterReset}
      />
      {loaded ?
        <Card>
          <Scrollbar>
            <TableContainer>
              <Table>
                <ListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  onRequestSort={handleRequestSort}
                  checkbox={false}
                />

                <TableBody>
                  {filteredList.map((row) => {
                        const { id, terminal_id, merchantName, action, mode, business_entity } = row;

                        return (
                          <StyledTableRow
                            hover
                            key={id}
                          >
                            <CustomTableCell
                              field={terminal_id}
                            />
                            <CustomTableCell
                              field={merchantName ?? ""}
                            />
                            <CustomTableCell
                              field={
                                business_entity ?? ""
                              }
                            />
                            <CustomTableCell
                              field={mode ?? ""}
                              id={id}
                            />
                            <CustomTableCell
                              field={action}
                            />
                            <TableCell>
                              <Button
                                variant="text"
                                color="secondary"
                                onClick={() => {
                                  setGlobalLoading(true);
                                  getTerminalDetailsWithActions(id, setPendingDetails, () => setGlobalLoading(false));
                                }}
                              >
                                View Detail
                              </Button>
                            </TableCell>
                          </StyledTableRow>
                        );
                      })}
                </TableBody>
                {isItemNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <Paper>
                          <Typography
                            gutterBottom
                            align="center"
                            variant="subtitle1"
                          >
                            There is no pending terminal at the moment.
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={terminalList.count}
            rowsPerPage={pagination.page_size}
            page={pagination.page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
        :
        <CircularLoader />
      }
      {selectedId && (
        <RejectPendingTerminal
          open={openRejectDialog}
          onClose={handleCloseDialog}
          selectedId={selectedId}
          setGroupList={renderData}
          setLoaded={setLoaded}
        />
      )}

      {pendingDetails && (
        <CommonDialog
          title="Terminal Details"
          onClose={() => setPendingDetails(null)}
          renderActions={() =>
            userRole.includes("Approve/Reject") && (
              <DialogActions>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setOpenRejectDialog(true);
                    setSelectedId(pendingDetails.id);
                  }}
                >
                  Reject
                </Button>
                <Button
                  variant="contained"
                  onClick={async () => {
                    setGlobalLoading(true);
                    Log.info(`Click button [Approve] - Approve Terminal by Id: ${pendingDetails.id}`)
                    await approvePendingTerminal(
                      pendingDetails.id,
                      renderData,
                      setLoaded
                    )
                    setPendingDetails(null);
                    setGlobalLoading(false);
                  }}
                  sx={{ ...styles.customButton }}
                >
                  Approve
                </Button>
              </DialogActions>
            )
          }
        >
          <TerminalDetails detailsData={pendingDetails} />
        </CommonDialog>
      )}
    </Box>
  );
};

export default PendingTerminal;
